import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Search, Plus, ChevronRight, ChevronDown, Trash2,
  Save, Globe, Send, AlertCircle, CheckCircle2, X
} from 'lucide-react';
import { deleteMetaByTags, getMetaByTags, listMeta, upsertMetaByTags } from '../api/meta';
import { PagedResponseMetaViewDto, UpsertMetaDto } from '../api/types';
import PageHeader from '../components/shared/PageHeader';
import tagsTreeData from '../../tags_tree.json';

interface TagTreeNode {
  tag: string;
  title?: string;
  children?: TagTreeNode[];
}

interface NotificationItem {
  id: string;
  type: 'success' | 'error' | 'info';
  message: string;
}

const LOCALES = [
  { code: 'en', label: 'EN' },
  { code: 'es', label: 'ES' },
  { code: 'de', label: 'DE' },
  { code: 'fr', label: 'FR' },
  { code: 'pt', label: 'PT' },
  { code: 'it', label: 'IT' },
  { code: 'nl', label: 'NL' }
];

const META_TITLE_MAX = 60;
const META_DESC_MAX = 160;

const defaultForm: UpsertMetaDto = { name: '', metaTitle: '' };

function flattenTree(nodes: TagTreeNode[], path: string[] = []): { node: TagTreeNode; path: string[] }[] {
  return nodes.flatMap(node => {
    const myPath = [...path, node.tag];
    return [{ node, path: myPath }, ...flattenTree(node.children ?? [], myPath)];
  });
}

const MetaPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({ query: '', locale: '', page: 1, limit: 100 });
  const [tagSearch, setTagSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['root']));
  const [selectedTagPath, setSelectedTagPath] = useState<string>('');
  const [selectedLocale, setSelectedLocale] = useState<string>('en');
  const [metaForm, setMetaForm] = useState<UpsertMetaDto & { metaDescription?: string }>(defaultForm);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const tagsTree: TagTreeNode[] = (tagsTreeData as { tagsTree?: TagTreeNode[] }).tagsTree ?? [];

  const flatTree = useMemo(() => flattenTree(tagsTree), [tagsTree]);

  const filteredNodes = useMemo(() => {
    const q = tagSearch.trim().toLowerCase();
    if (!q) return null;
    return new Set(
      flatTree
        .filter(({ node }) => node.tag.toLowerCase().includes(q) || (node.title ?? '').toLowerCase().includes(q))
        .map(({ path }) => path.join('-'))
    );
  }, [flatTree, tagSearch]);

  const { data: allMeta } = useQuery<PagedResponseMetaViewDto>({
    queryKey: ['meta', filters],
    queryFn: () => listMeta({
      query: filters.query || undefined,
      locale: filters.locale || undefined,
      page: filters.page,
      limit: filters.limit
    })
  });

  // Stats
  const stats = useMemo(() => {
    const total = allMeta?.total ?? 0;
    const items = allMeta?.items ?? [];
    const locales = new Set(items.map(item => item.locale || 'default')).size;
    const complete = items.filter(item => item.name && item.metaTitle).length;
    const completeRate = items.length > 0 ? Math.round((complete / items.length) * 100) : 0;
    return { total, locales, complete, completeRate, missing: items.length - complete };
  }, [allMeta]);

  const addNotification = (type: NotificationItem['type'], message: string) => {
    const id = Date.now().toString();
    setNotifications(prev => [...prev, { id, type, message }]);
    setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== id)), 4000);
  };

  const getMutation = useMutation({
    mutationFn: ({ tags, locale }: { tags: string; locale: string }) => getMetaByTags(tags, locale),
    onSuccess: (response) => {
      setMetaForm({
        name: response.name ?? '',
        metaTitle: response.metaTitle ?? '',
        metaDescription: ''  // API doesn't yet return desc; preserved client-side
      });
    },
    onError: () => {
      setMetaForm(defaultForm);
    }
  });

  const upsertMutation = useMutation({
    mutationFn: ({ tags, payload, locale }: { tags: string; payload: UpsertMetaDto; locale: string }) =>
      upsertMetaByTags(tags, payload, locale),
    onSuccess: () => {
      addNotification('success', 'Saved');
      queryClient.invalidateQueries({ queryKey: ['meta'] });
    },
    onError: () => addNotification('error', 'Failed to save meta')
  });

  const deleteMutation = useMutation({
    mutationFn: ({ tags, locale }: { tags: string; locale: string }) => deleteMetaByTags(tags, locale),
    onSuccess: () => {
      addNotification('success', 'Deleted');
      setMetaForm(defaultForm);
      queryClient.invalidateQueries({ queryKey: ['meta'] });
    },
    onError: () => addNotification('error', 'Failed to delete meta')
  });

  useEffect(() => {
    if (selectedTagPath) {
      getMutation.mutate({ tags: selectedTagPath, locale: selectedLocale });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTagPath, selectedLocale]);

  const selectTag = (path: string[]) => {
    setSelectedTagPath(path.join('-'));
  };

  const toggleExpanded = (path: string[]) => {
    const key = path.join('-');
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleSave = () => {
    if (!selectedTagPath) return;
    upsertMutation.mutate({
      tags: selectedTagPath,
      payload: { name: metaForm.name, metaTitle: metaForm.metaTitle },
      locale: selectedLocale
    });
  };

  const handleDelete = () => {
    if (!selectedTagPath || !window.confirm('Delete this meta entry?')) return;
    deleteMutation.mutate({ tags: selectedTagPath, locale: selectedLocale });
  };

  // Google preview values
  const previewTitle = metaForm.metaTitle?.trim() || metaForm.name?.trim() || `${selectedTagPath || 'tag-combo'} · OnlySeeker`;
  const previewUrl = `onlyseeker.io/${selectedLocale}/best/${selectedTagPath || 'tag-combo'}`;
  const previewDesc = metaForm.metaDescription?.trim() || 'Meta description will appear here.';

  const matchCount = useMemo(() => {
    if (!allMeta || !selectedTagPath) return 0;
    return allMeta.items.filter(it => it.tags === selectedTagPath).length;
  }, [allMeta, selectedTagPath]);

  const actions = (
    <>
      <button className="hf-btn" onClick={() => {
        setSelectedTagPath('');
        setMetaForm(defaultForm);
      }}>Clear</button>
      <button
        className="hf-btn primary"
        onClick={() => {
          const tags = window.prompt('Enter tag combo (e.g., blonde-teen):');
          if (tags) {
            setSelectedTagPath(tags.trim());
            setMetaForm(defaultForm);
          }
        }}
      >
        <Plus size={14} /> New meta
      </button>
    </>
  );

  return (
    <>
      <PageHeader
        title="Meta Tags"
        subtitle="Title & description shown on category pages · per tag combo · per locale."
        actions={actions}
      />

      <div className="hf-content">
        {/* Toasts */}
        {notifications.length > 0 && (
          <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 100, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {notifications.map(n => (
              <div
                key={n.id}
                className={`hf-banner ${n.type === 'success' ? 'success' : n.type === 'error' ? 'danger' : ''}`}
                style={{ minWidth: 280, boxShadow: 'var(--shadow-lg)' }}
              >
                {n.type === 'success' && <CheckCircle2 size={14} style={{ color: 'var(--success)' }} />}
                {n.type === 'error' && <AlertCircle size={14} style={{ color: 'var(--danger)' }} />}
                <span>{n.message}</span>
              </div>
            ))}
          </div>
        )}

        {/* KPI strip */}
        <div className="hf-kpi-strip">
          <div className="hf-kpi">
            <span className="hf-kpi-label">Tag entries</span>
            <span className="hf-kpi-value">{stats.total}</span>
          </div>
          <div className="hf-kpi">
            <span className="hf-kpi-label">Locales</span>
            <span className="hf-kpi-value">{stats.locales}</span>
            <span className="hf-kpi-sub">{LOCALES.map(l => l.label).join(' · ')}</span>
          </div>
          <div className="hf-kpi accent">
            <span className="hf-kpi-label">Complete</span>
            <span className="hf-kpi-value">{stats.completeRate}%</span>
            <span className="hf-kpi-sub">{stats.complete} of {allMeta?.items.length ?? 0}</span>
          </div>
          <div className="hf-kpi">
            <span className="hf-kpi-label">Missing meta</span>
            <span className="hf-kpi-value">{stats.missing}</span>
          </div>
        </div>

        {/* 2-col body */}
        <div className="hf-three-col" style={{ gridTemplateColumns: '280px 1fr' }}>
          {/* Tag tree card */}
          <div className="hf-card">
            <div className="hf-card-header">
              <h3 className="hf-card-title">Tag tree</h3>
              <span className="hf-count">{flatTree.length}</span>
            </div>
            <div className="hf-card-body" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div className="hf-search">
                <Search size={14} />
                <input
                  value={tagSearch}
                  onChange={(e) => setTagSearch(e.target.value)}
                  placeholder="search tags…"
                />
              </div>
              <div style={{ maxHeight: 460, overflow: 'auto', padding: '4px 2px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {tagsTree.map((node, i) => (
                  <TagNode
                    key={`${node.tag}-${i}`}
                    node={node}
                    path={[node.tag]}
                    expanded={expanded}
                    onToggle={toggleExpanded}
                    onSelect={selectTag}
                    selectedTagPath={selectedTagPath}
                    filteredKeys={filteredNodes}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Editor card */}
          <div className="hf-card">
            <div className="hf-card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h3 className="hf-card-title">
                  {selectedTagPath ? (
                    <>
                      <span className="hf-mono" style={{ color: 'var(--fg-1)' }}>{selectedTagPath}</span>
                      <span className="hf-count" style={{ marginLeft: 8 }}>match: {matchCount}</span>
                    </>
                  ) : (
                    'Pick a tag from the tree'
                  )}
                </h3>
              </div>
              {/* Locale switcher */}
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {LOCALES.map(l => (
                  <button
                    key={l.code}
                    type="button"
                    className={`hf-chip ${selectedLocale === l.code ? 'on' : ''}`}
                    onClick={() => setSelectedLocale(l.code)}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="hf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {!selectedTagPath ? (
                <div style={{ textAlign: 'center', color: 'var(--fg-3)', padding: '40px 0' }}>
                  <Globe size={32} style={{ marginBottom: 8 }} />
                  <div>Select a tag from the tree to edit its meta.</div>
                </div>
              ) : (
                <>
                  {/* Meta Title */}
                  <div>
                    <div className="hf-label-row">
                      <span className="hf-label">Meta title</span>
                      <span className="hf-help" style={{ marginTop: 0, color: (metaForm.metaTitle?.length ?? 0) > META_TITLE_MAX ? 'var(--danger)' : 'var(--fg-3)' }}>
                        <span className="hf-mono">{metaForm.metaTitle?.length ?? 0}</span>/{META_TITLE_MAX}
                      </span>
                    </div>
                    <div className="hf-input">
                      <input
                        type="text"
                        value={metaForm.metaTitle ?? ''}
                        onChange={(e) => setMetaForm({ ...metaForm, metaTitle: e.target.value })}
                        placeholder="Best onlyfans for…"
                      />
                    </div>
                  </div>

                  {/* Meta description */}
                  <div>
                    <div className="hf-label-row">
                      <span className="hf-label">Meta description</span>
                      <span className="hf-help" style={{ marginTop: 0, color: (metaForm.metaDescription?.length ?? 0) > META_DESC_MAX ? 'var(--danger)' : 'var(--fg-3)' }}>
                        <span className="hf-mono">{metaForm.metaDescription?.length ?? 0}</span>/{META_DESC_MAX}
                      </span>
                    </div>
                    <textarea
                      className="hf-textarea"
                      rows={3}
                      maxLength={META_DESC_MAX}
                      value={metaForm.metaDescription ?? ''}
                      onChange={(e) => setMetaForm({ ...metaForm, metaDescription: e.target.value })}
                      placeholder="Discover the best…"
                    />
                  </div>

                  {/* Display name */}
                  <div>
                    <div className="hf-label-row">
                      <span className="hf-label">Display name</span>
                    </div>
                    <div className="hf-input">
                      <input
                        type="text"
                        value={metaForm.name ?? ''}
                        onChange={(e) => setMetaForm({ ...metaForm, name: e.target.value })}
                        placeholder="Human-friendly category title"
                      />
                    </div>
                  </div>

                  <div className="hf-divider dashed" />

                  {/* Google preview */}
                  <div>
                    <span className="hf-label">Google preview</span>
                    <div className="hf-feature-box muted" style={{ marginTop: 6, padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--fg-3)', fontSize: 11 }}>
                        <Globe size={11} />
                        <span className="hf-mono">{previewUrl}</span>
                      </div>
                      <div style={{ marginTop: 4, color: '#1a0dab', fontSize: 18, lineHeight: 1.3, fontWeight: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {previewTitle}
                      </div>
                      <div style={{ marginTop: 4, color: 'var(--fg-2)', fontSize: 13, lineHeight: 1.45 }}>
                        {previewDesc}
                      </div>
                    </div>
                  </div>

                  {/* Footer actions */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                    <button
                      className="hf-btn ghost"
                      onClick={handleDelete}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                    <div style={{ flex: 1 }} />
                    <button
                      className="hf-btn"
                      onClick={handleSave}
                      disabled={upsertMutation.isPending}
                    >
                      <Save size={14} /> Save draft
                    </button>
                    <button
                      className="hf-btn primary"
                      onClick={handleSave}
                      disabled={upsertMutation.isPending}
                    >
                      <Send size={14} /> Publish
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

interface TagNodeProps {
  node: TagTreeNode;
  path: string[];
  expanded: Set<string>;
  onToggle: (path: string[]) => void;
  onSelect: (path: string[]) => void;
  selectedTagPath: string;
  filteredKeys: Set<string> | null;
}

const TagNode: React.FC<TagNodeProps> = ({ node, path, expanded, onToggle, onSelect, selectedTagPath, filteredKeys }) => {
  const key = path.join('-');
  const hasChildren = (node.children ?? []).length > 0;
  const isExpanded = expanded.has(key);
  const isSelected = selectedTagPath === key;
  const isVisible = !filteredKeys || filteredKeys.has(key);

  // If filtered and not visible, still render if has visible descendants
  const hasVisibleDescendant = useMemo(() => {
    if (!filteredKeys) return true;
    if (filteredKeys.has(key)) return true;
    const queue = [...(node.children ?? [])];
    while (queue.length) {
      const n = queue.shift()!;
      if (filteredKeys.has([...path, n.tag].join('-'))) return true;
      queue.push(...(n.children ?? []));
    }
    return false;
  }, [filteredKeys, key, node, path]);

  if (filteredKeys && !hasVisibleDescendant) return null;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '4px 6px',
          borderRadius: 'var(--r-1)',
          cursor: 'pointer',
          background: isSelected ? 'var(--highlight-soft)' : 'transparent',
          color: isSelected ? 'var(--accent)' : 'var(--fg-1)',
          fontSize: 12.5,
          fontWeight: isSelected ? 500 : 400
        }}
        onClick={() => onSelect(path)}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggle(path); }}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--fg-3)', display: 'flex', alignItems: 'center' }}
          >
            {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </button>
        ) : (
          <span style={{ width: 12 }} />
        )}
        <span
          style={{
            flex: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
          className={!isVisible && filteredKeys ? '' : ''}
        >
          {node.title || node.tag}
        </span>
        {hasChildren && (
          <span className="hf-count" style={{ fontSize: 10 }}>{(node.children ?? []).length}</span>
        )}
      </div>
      {isExpanded && hasChildren && (
        <div style={{ paddingLeft: 12 }}>
          {(node.children ?? []).map((child, i) => (
            <TagNode
              key={`${child.tag}-${i}`}
              node={child}
              path={[...path, child.tag]}
              expanded={expanded}
              onToggle={onToggle}
              onSelect={onSelect}
              selectedTagPath={selectedTagPath}
              filteredKeys={filteredKeys}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MetaPage;
